import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Timer,
  Plus,
  Pencil,
  Trash2,
  Search,
  Loader2,
  ExternalLink,
  ClipboardPaste,
  CheckCircle2,
  XCircle,
  Radio,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

/**
 * Admin section for managing Speedhive-connected categories.
 *
 * A category is a { name, org_id, organizer_name } record. Categories with
 * an `org_id` populate the "Cronometraje" (Speedhive) importer dropdown so
 * end users don't need to know the org id or paste any Speedhive URLs.
 * Categories without an `org_id` are only accessible via manual XLS/Drive.
 */
const AdminSpeedhiveCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null → create; object → edit
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [probing, setProbing] = useState(false);

  const emptyForm = {
    name: '',
    org_id: '',
    organizer_name: '',
    live_event_id: '',
    notes: '',
    is_active: true,
    resolve_url: '',
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/speedhive/categories`, {
        params: { include_inactive: true },
      });
      setCategories(res.data || []);
    } catch (err) {
      toast.error('No pude cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditorOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name || '',
      org_id: cat.org_id ? String(cat.org_id) : '',
      organizer_name: cat.organizer_name || '',
      live_event_id: cat.live_event_id || '',
      notes: cat.notes || '',
      is_active: !!cat.is_active,
      resolve_url: '',
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const resolveOrg = async () => {
    const url = (form.resolve_url || '').trim();
    if (!url) {
      toast.error('Pegá un link o ID de evento para resolver el org_id');
      return;
    }
    setResolving(true);
    try {
      const res = await axios.post(
        `${API}/speedhive/admin/resolve-org`,
        null,
        { params: { url } },
      );
      const { org_id, organizer_name, event_name, live_event_id } = res.data || {};
      if (!org_id && !live_event_id) {
        toast.error('No pude resolver ningún dato desde ese link');
        return;
      }
      setForm((prev) => ({
        ...prev,
        org_id: org_id ? String(org_id) : prev.org_id,
        // Only auto-fill organizer if the user hasn't typed one already.
        organizer_name: prev.organizer_name || organizer_name || '',
        // Auto-fill live_event_id when we got one (e.g. from /livetiming/… URL).
        live_event_id: live_event_id || prev.live_event_id,
      }));
      if (live_event_id && !org_id) {
        toast.success(`Live timing detectado: ${live_event_id} · completá el ORG_ID manualmente si querés importar históricos`);
      } else {
        toast.success(
          `Resuelto: ${organizer_name || 'org'} (#${org_id}) · ${event_name || ''}`,
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resolver');
    } finally {
      setResolving(false);
    }
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('El nombre es obligatorio');
      return;
    }
    const payload = {
      name,
      org_id: form.org_id ? Number(form.org_id) : null,
      organizer_name: form.organizer_name.trim() || null,
      live_event_id: form.live_event_id.trim() || null,
      notes: form.notes.trim() || null,
      is_active: form.is_active,
    };
    if (payload.org_id != null && Number.isNaN(payload.org_id)) {
      toast.error('org_id debe ser numérico');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(
          `${API}/speedhive/admin/categories/${editing.id}`,
          payload,
        );
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/speedhive/admin/categories`, payload);
        toast.success('Categoría creada');
      }
      await load();
      closeEditor();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const probeLiveEventId = async () => {
    const leid = (form.live_event_id || '').trim();
    if (!leid) {
      toast.error('Cargá primero el Live Event ID que querés probar');
      return;
    }
    setProbing(true);
    try {
      const res = await axios.post(`${API}/speedhive/live/probe`, {
        live_event_id: leid,
      });
      if (res.data?.ok) {
        toast.success(res.data.message || 'Live Timing responde OK');
      } else {
        toast.error(
          res.data?.message ||
            'No se pudo conectar con Live Timing con ese ID',
        );
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          'Error al probar el Live Event ID',
      );
    } finally {
      setProbing(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/speedhive/admin/categories/${confirmDelete.id}`,
      );
      toast.success('Categoría eliminada');
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      className="bg-neutral-900 border-neutral-700"
      data-testid="admin-speedhive-categories"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <Timer className="w-5 h-5 text-red-500" />
            Categorías · Cronometraje (Speedhive)
          </CardTitle>
          <CardDescription>
            Alta y edición de las categorías conectadas al feed de{' '}
            <a
              href="https://speedhive.mylaps.com"
              target="_blank"
              rel="noreferrer"
              className="text-red-400 hover:text-red-300 inline-flex items-center gap-1"
            >
              speedhive.mylaps.com
              <ExternalLink className="w-3 h-3" />
            </a>
            . Las categorías con <code className="text-red-400">org_id</code>{' '}
            aparecen en el importador. Las que no lo tengan sólo se cargarán
            por Planillas oficiales.
          </CardDescription>
        </div>
        <Button
          onClick={openCreate}
          className="bg-red-600 hover:bg-red-700 whitespace-nowrap"
          data-testid="admin-cat-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Nueva categoría
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-gray-400">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
            Cargando…
          </div>
        ) : categories.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No hay categorías configuradas todavía.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-neutral-800">
                  <th className="py-2 pr-3">Nombre</th>
                  <th className="py-2 pr-3">org_id</th>
                  <th className="py-2 pr-3">Organizador</th>
                  <th className="py-2 pr-3">Live Timing</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 pr-3 w-24 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800/60">
                {categories.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-neutral-800/30"
                    data-testid={`admin-cat-row-${c.id}`}
                  >
                    <td className="py-2 pr-3">
                      <div className="text-white font-medium">{c.name}</div>
                      {c.notes && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {c.notes}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-300 font-mono">
                      {c.org_id ? (
                        <span>#{c.org_id}</span>
                      ) : (
                        <span className="text-gray-600 italic">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-300">
                      {c.organizer_name || (
                        <span className="text-gray-600 italic">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {c.live_event_id ? (
                        <Badge className="bg-red-600/20 text-red-400 text-[10px] px-1.5 py-0 inline-flex items-center gap-1">
                          <Radio className="w-3 h-3" />
                          <span
                            className="font-mono truncate max-w-[140px]"
                            title={c.live_event_id}
                          >
                            {c.live_event_id.length > 18
                              ? `${c.live_event_id.slice(0, 8)}…${c.live_event_id.slice(-6)}`
                              : c.live_event_id}
                          </span>
                        </Badge>
                      ) : (
                        <span className="text-gray-600 italic text-xs">
                          Sin captura auto
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {c.is_active ? (
                        <Badge className="bg-green-600/20 text-green-400 text-[10px] px-1.5 py-0 inline-flex items-center gap-0.5">
                          <CheckCircle2 className="w-3 h-3" />
                          Activa
                        </Badge>
                      ) : (
                        <Badge className="bg-neutral-700/70 text-gray-400 text-[10px] px-1.5 py-0 inline-flex items-center gap-0.5">
                          <XCircle className="w-3 h-3" />
                          Inactiva
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(c)}
                          className="p-1.5 rounded hover:bg-neutral-800 text-gray-400 hover:text-white transition-colors"
                          title="Editar"
                          data-testid={`admin-cat-edit-${c.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(c)}
                          className="p-1.5 rounded hover:bg-red-900/40 text-gray-400 hover:text-red-300 transition-colors"
                          title="Eliminar"
                          data-testid={`admin-cat-delete-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Editor dialog (create + edit) */}
      <Dialog open={editorOpen} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent
          className="bg-neutral-900 border-neutral-700 text-white max-w-lg"
          data-testid="admin-cat-editor"
        >
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Editar categoría' : 'Nueva categoría'}
            </DialogTitle>
            <DialogDescription className="text-gray-400 pt-1">
              El <code className="text-red-400">org_id</code> es opcional. Si
              no lo cargás, la categoría queda solo para Planillas oficiales.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Auto-resolve helper */}
            <div className="rounded-lg border border-neutral-700 bg-neutral-800/40 p-3 space-y-2">
              <Label className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                <ClipboardPaste className="w-3.5 h-3.5" />
                Auto-completar desde link de evento
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  value={form.resolve_url}
                  onChange={(e) =>
                    setForm({ ...form, resolve_url: e.target.value })
                  }
                  placeholder="speedhive.mylaps.com/events/3518475"
                  className="flex-1 min-w-[220px] bg-neutral-900 border-neutral-700 text-white placeholder:text-gray-500"
                  data-testid="admin-cat-resolve-input"
                />
                <Button
                  size="sm"
                  onClick={resolveOrg}
                  disabled={resolving}
                  variant="outline"
                  className="border-red-900/50 text-red-400 hover:bg-red-950/30"
                  data-testid="admin-cat-resolve-btn"
                >
                  {resolving ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Resolviendo…
                    </>
                  ) : (
                    <>
                      <Search className="w-3.5 h-3.5 mr-1.5" />
                      Resolver
                    </>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-gray-500">
                Pegá cualquier link de un evento de esta categoría y completo
                automáticamente <code>org_id</code> y <code>organizador</code>.
              </p>
            </div>

            {/* Fields */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-400">
                Nombre de la categoría <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ej: Turismo Carretera"
                className="bg-neutral-800 border-neutral-700 text-white mt-1"
                data-testid="admin-cat-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-gray-400">
                  org_id (Speedhive)
                </Label>
                <Input
                  type="number"
                  value={form.org_id}
                  onChange={(e) =>
                    setForm({ ...form, org_id: e.target.value })
                  }
                  placeholder="ej: 381036"
                  className="bg-neutral-800 border-neutral-700 text-white mt-1 font-mono"
                  data-testid="admin-cat-orgid-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-gray-400">
                  Organizador / Cronometrador
                </Label>
                <Input
                  value={form.organizer_name}
                  onChange={(e) =>
                    setForm({ ...form, organizer_name: e.target.value })
                  }
                  placeholder="ej: Realtime Group SRL"
                  className="bg-neutral-800 border-neutral-700 text-white mt-1"
                  data-testid="admin-cat-organizer-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                <Radio className="w-3.5 h-3.5 text-red-400" />
                Live Event ID (Speedhive Live Timing)
              </Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Input
                  value={form.live_event_id}
                  onChange={(e) =>
                    setForm({ ...form, live_event_id: e.target.value })
                  }
                  placeholder="ej: A478B73F3AA16606-2147487551"
                  className="flex-1 min-w-[220px] bg-neutral-800 border-neutral-700 text-white font-mono text-xs"
                  data-testid="admin-cat-liveid-input"
                />
                <Button
                  size="sm"
                  onClick={probeLiveEventId}
                  disabled={probing || !form.live_event_id.trim()}
                  variant="outline"
                  className="border-red-900/50 text-red-400 hover:bg-red-950/30"
                  data-testid="admin-cat-liveid-probe-btn"
                >
                  {probing ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Probando…
                    </>
                  ) : (
                    <>
                      <Zap className="w-3.5 h-3.5 mr-1.5" />
                      Probar
                    </>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                Con este ID cargado, el sistema capturará automáticamente
                cada sesión de la fecha (ensayos, clasificación, final) con
                parciales reales — sin necesidad de apretar nada.
                Se admite cualquier formato: si Speedhive rechaza el ID, el
                botón <b>Probar</b> te lo va a avisar.
              </p>
            </div>

            <div>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="ej: Solo desde temporada 2026"
                className="bg-neutral-800 border-neutral-700 text-white mt-1"
                data-testid="admin-cat-notes-input"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-700 bg-neutral-800/40 px-3 py-2">
              <div>
                <div className="text-sm text-white">Activa</div>
                <div className="text-[11px] text-gray-500">
                  Sólo las activas aparecen en el importador
                </div>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="admin-cat-active-switch"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeEditor}
              disabled={saving}
              className="border-neutral-700 text-gray-200 hover:bg-neutral-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-cat-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Guardando…
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <DialogContent
          className="bg-neutral-900 border-neutral-700 text-white"
          data-testid="admin-cat-delete-dialog"
        >
          <DialogHeader>
            <DialogTitle>Eliminar categoría</DialogTitle>
            <DialogDescription className="text-gray-400 pt-1">
              ¿Seguro que querés eliminar{' '}
              <b className="text-white">{confirmDelete?.name}</b>? Los usuarios
              dejarán de verla en el importador.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
              className="border-neutral-700 text-gray-200 hover:bg-neutral-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-cat-delete-confirm"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Eliminando…
                </>
              ) : (
                'Eliminar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminSpeedhiveCategories;
